import { Area } from '@/tmapBasicInfo/floorMapInfo/Area'
import { Divider } from '@/tmapBasicInfo/floorMapInfo/Divider'
import { Room } from '@/tmapBasicInfo/floorMapInfo/Room'
import { Coordinate } from '@/types/floorMap/Coodinate'

/**
 * ドラクエ9 宝の地図の1フロア分のフロアマップデータを保持するクラス
 * @class FloorMapData
 */
class FloorMapData {
  /**
   * @type {Area[]} 領域を格納する配列
   */
  private _areas: Area[] = []

  /**
   * @type {Divider[]} 仕切り棒を格納する配列
   */
  private _dividers: Divider[] = []

  /**
   * @type {Room[]} 部屋を格納する配列
   */
  private _rooms: Room[] = []

  /**
   * @type {Coordinate[]} 宝箱の座標を格納する配列
   */
  private _treasureBoxCoordinates: Coordinate[] = []

  /**
   * @type {number} 地図ランク
   */
  private _mapRank: number = -1

  /**
   * @type {number} 地図シード
   */
  private _mapSeed: number = -1

  /**
   * @type {number} 当該フロアの階数
   */
  private _floor: number = -1

  /**
   * @type {number} 当該フロアの敵ランク
   */
  private _enemyRank: number = -1

  /**
   * @type {number} 当該フロアの幅
   */
  private _width: number = -1

  /**
   * @type {number} 当該フロアの高さ
   */
  private _height: number = -1

  /**
   * @type {Coordinate} 当該フロアの上り階段の座標
   */
  private _upStairsCoordinate: Coordinate = { x: 0, y: 0 }

  /**
   * @type {Coordinate} 当該フロアの下り階段の座標
   */
  private _downStairsCoordinate: Coordinate = { x: 0, y: 0 }

  /**
   * @type {boolean} 部屋の変形時にフロア全体から計算されたかどうか
   */
  private _isCalculatedAsWhole: boolean = false

  /**
   * @type {number[]} 当該フロアのフロアマップ
   */
  private _floorMap: number[] = new Array(256)

  /**
   * 領域を格納する配列のgetter
   * @returns {Area[]} 領域を格納する配列
   */
  public get areas(): Area[] {
    return [...this._areas]
  }

  /**
   * 領域を格納する配列のsetter
   * @param {Area[]} value 領域を格納する配列
   */
  public set areas(value: Area[]) {
    this._areas = [...value]
  }

  /**
   * 仕切り棒を格納する配列のgetter
   * @returns {Divider[]} 仕切り棒を格納する配列
   */
  public get dividers(): Divider[] {
    return [...this._dividers]
  }

  /**
   * 仕切り棒を格納する配列のsetter
   * @param {Divider[]} value 仕切り棒を格納する配列
   */
  public set dividers(value: Divider[]) {
    this._dividers = [...value]
  }

  /**
   * 部屋を格納する配列のgetter
   * @returns {Room[]} 部屋を格納する配列
   */
  public get rooms(): Room[] {
    return [...this._rooms]
  }

  /**
   * 部屋を格納する配列のsetter
   * @param {Room[]} value 部屋を格納する配列
   */
  public set rooms(value: Room[]) {
    this._rooms = [...value]
  }

  /**
   * 宝箱の座標を格納する配列のgetter
   * @returns {Coordinate[]} 宝箱の座標を格納する配列
   */
  public get treasureBoxCoordinates(): Coordinate[] {
    return [...this._treasureBoxCoordinates]
  }

  /**
   * 宝箱の座標を格納する配列のsetter
   * @param {Coordinate[]} value 宝箱の座標を格納する配列
   */
  public set treasureBoxCoordinates(value: Coordinate[]) {
    this._treasureBoxCoordinates = [...value]
  }

  /**
   * 地図ランクのgetter
   * @returns {number} 地図ランク
   */
  public get mapRank(): number {
    return this._mapRank
  }

  /**
   * 地図ランクのsetter
   * @param {number} value 地図ランク
   */
  public set mapRank(value: number) {
    this._mapRank = value
  }

  /**
   * 地図シードのgetter
   * @returns {number} 地図シード
   */
  public get mapSeed(): number {
    return this._mapSeed
  }

  /**
   * 地図シードのsetter
   * @param {number} value 地図シード
   */
  public set mapSeed(value: number) {
    this._mapSeed = value
  }

  /**
   * 当該フロアの階数のgetter
   * @returns {number} 当該フロアの階数
   */
  public get floor(): number {
    return this._floor
  }

  /**
   * 当該フロアの階数のsetter
   * @param {number} value 当該フロアの階数
   */
  public set floor(value: number) {
    this._floor = value
  }

  /**
   * 当該フロアの敵ランクのgetter
   * @returns {number} 当該フロアの敵ランク
   */
  public get enemyRank(): number {
    return this._enemyRank
  }

  /**
   * 当該フロアの敵ランクのsetter
   * @param {number} value 当該フロアの敵ランク
   */
  public set enemyRank(value: number) {
    this._enemyRank = value
  }

  /**
   * 当該フロアの幅のgetter
   * @returns {number} 当該フロアの幅
   */
  public get width(): number {
    return this._width
  }

  /**
   * 当該フロアの幅のsetter
   * @param {number} value 当該フロアの幅
   */
  public set width(value: number) {
    this._width = value
  }

  /**
   * 当該フロアの高さのgetter
   * @returns {number} 当該フロアの高さ
   */
  public get height(): number {
    return this._height
  }

  /**
   * 当該フロアの高さのsetter
   * @param {number} value 当該フロアの高さ
   */
  public set height(value: number) {
    this._height = value
  }

  /**
   * 当該フロアの上り階段の座標のgetter
   * @returns {Coordinate} 当該フロアの上り階段の座標
   */
  public get upStairsCoordinate(): Coordinate {
    return { ...this._upStairsCoordinate }
  }

  /**
   * 当該フロアの上り階段の座標のsetter
   * @param {Coordinate} value 当該フロアの上り階段の座標
   */
  public set upStairsCoordinate(value: Coordinate) {
    this._upStairsCoordinate = { ...value }
  }

  /**
   * 当該フロアの下り階段の座標のgetter
   * @returns {Coordinate} 当該フロアの下り階段の座標
   */
  public get downStairsCoordinate(): Coordinate {
    return { ...this._downStairsCoordinate }
  }

  /**
   * 当該フロアの下り階段の座標のsetter
   * @param {Coordinate} value 当該フロアの下り階段の座標
   */
  public set downStairsCoordinate(value: Coordinate) {
    this._downStairsCoordinate = { ...value }
  }

  /**
   * 部屋の変形時にフロア全体から計算されたかどうかのgetter
   * @returns {boolean} 部屋の変形時にフロア全体から計算されたかどうか
   */
  public get isCalculatedAsWhole(): boolean {
    return this._isCalculatedAsWhole
  }

  /**
   * 部屋の変形時にフロア全体から計算されたかどうかのsetter
   * @param {boolean} value 部屋の変形時にフロア全体から計算されたかどうか
   */
  public set isCalculatedAsWhole(value: boolean) {
    this._isCalculatedAsWhole = value
  }

  /**
   * 座標を指定して当該フロアのフロアマップの値を返却
   * @param {number} x x座標
   * @param {number} y y座標
   * @returns 当該フロアマップの指定座標の値
   */
  public getFloorMapValue(x: number, y: number): number {
    return this._floorMap[y * 16 + x]
  }

  /**
   * 座標と更新値を指定して当該フロアのフロアマップの値を更新
   * @param {number} x x座標
   * @param {number} y y座標
   * @param {number} value 更新値
   */
  public setFloorMapValue(x: number, y: number, value: number): void {
    this._floorMap[y * 16 + x] = value
  }
}

export { FloorMapData }
